import { useEffect, useRef, useState } from 'react';
import { Pencil, Trash2, Upload } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTranslation } from '@/hooks/useTranslation';
import { useAdStickers } from '@/hooks/useAdStickers';
import { adStickerService } from '@/services/adStickerService';
import type { RichTextItem } from '@/types/ads';
import { stickers } from '@/constants/richMessage';
import RichMessagePreview from './RichMessagePreview';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';

function UploadPreview({ file }: { file: File }) {
  const image = useRef<HTMLImageElement>(null);
  useEffect(() => {
    const url = URL.createObjectURL(file);
    if (image.current) image.current.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);
  return <img ref={image} alt="" className="mx-auto h-20 w-20 object-contain" />;
}

export default function StickerLibraryPicker({ onSelect, canSelectImage, onBusy }: {
  onSelect: (item: RichTextItem) => void; canSelectImage: boolean; onBusy: (busy: boolean) => void;
}) {
  const { t } = useTranslation();
  const l = t.richMessage;
  const s = t.stickerLibrary;
  const [tab, setTab] = useState<'builtin' | 'library'>('builtin');
  const [search, setSearch] = useState('');
  const [name, setName] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [uploadedUrl, setUploadedUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [deleting, setDeleting] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const { query, create, rename, remove } = useAdStickers();
  const busy = uploading || create.isPending || rename.isPending || remove.isPending;
  const run = async (action: () => Promise<unknown>) => {
    onBusy(true);
    try { await action(); } catch { toast.error(s.failed); } finally { onBusy(false); }
  };
  const save = () => run(async () => {
    if (!file || !name.trim()) return;
    setUploading(true);
    try {
      const url = uploadedUrl || await adStickerService.upload(file);
      setUploadedUrl(url);
      await create.mutateAsync({ name: name.trim(), imageUrl: url });
      setFile(null); setName(''); setUploadedUrl('');
    } finally { setUploading(false); }
  });
  return <fieldset disabled={busy} className="space-y-3">
    <div role="tablist" className="flex border-b">
      {(['builtin', 'library'] as const).map(value => <button type="button" role="tab" key={value}
        aria-selected={tab === value} onClick={() => setTab(value)}
        className={`cursor-pointer border-b-2 px-3 py-2 text-sm ${tab === value ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500'}`}>
        {value === 'builtin' ? s.builtin : s.mine}
      </button>)}
    </div>
    <Input size="sm" aria-label={l.search} placeholder={l.search} value={search} onChange={event => setSearch(event.target.value)} />
    {tab === 'builtin' ? <div className="grid grid-cols-3 gap-2">
      {Object.entries(stickers).filter(([id]) => l[id as 'party'].toLocaleLowerCase().includes(search.toLocaleLowerCase()))
        .map(([id]) => <button type="button" key={id} aria-label={l[id as 'party']}
          className="flex cursor-pointer flex-col items-center gap-3 rounded-xl border border-gray-100 p-4 hover:bg-indigo-50"
          onClick={() => onSelect({ kind: 'STICKER', sticker: id })}>
          <span className="text-3xl"><RichMessagePreview items={[{ kind: 'STICKER', sticker: id }]} /></span>
          <span className="text-xs text-gray-600">{l[id as 'party']}</span>
        </button>)}
    </div> : <>
      <p className="text-xs text-gray-500">{s.help}</p>
      <input ref={fileInput} type="file" accept="image/gif,image/webp" className="hidden" onChange={async event => {
        const next = event.target.files?.[0]; event.target.value = '';
        if (!next) return;
        const bytes = new Uint8Array(await next.slice(0, 30).arrayBuffer());
        const signature = String.fromCharCode(...bytes.slice(0, 6));
        const width = bytes[6] | bytes[7] << 8, height = bytes[8] | bytes[9] << 8;
        const webp = String.fromCharCode(...bytes.slice(0, 4)) === 'RIFF' && String.fromCharCode(...bytes.slice(8, 12)) === 'WEBP';
        const gif = ['GIF87a', 'GIF89a'].includes(signature);
        if ((!gif && !webp) || next.size > 5 * 1024 * 1024 || (gif && (!width || !height || width > 1024 || height > 1024))) {
          toast.error(s.invalid); return;
        }
        setFile(next); setUploadedUrl(''); setName(next.name.replace(/\.(gif|webp)$/i, '').slice(0, 80));
      }} />
      <Button type="button" variant="outline" size="sm" onClick={() => fileInput.current?.click()}><Upload className="mr-2 h-4 w-4" />{s.upload}</Button>
      {file && <div className="space-y-2 rounded border p-3">
        <UploadPreview file={file} />
        <p className="truncate text-xs text-gray-500">{file.name}</p>
        <Input size="sm" label={s.name} aria-label={s.name} maxLength={80} value={name} onChange={event => setName(event.target.value)} />
        <div className="flex gap-2">
          <Button type="button" size="sm" disabled={!name.trim()} loading={busy} onClick={save}>{s.save}</Button>
          <Button type="button" size="sm" variant="outline" onClick={() => { setFile(null); setUploadedUrl(''); }}>{s.cancel}</Button>
        </div>
      </div>}
      {query.isPending ? <p className="text-sm">{s.loading}</p> : query.isError ? <button type="button" className="cursor-pointer text-sm text-red-600" onClick={() => query.refetch()}>{s.retry}</button> : <>
        {!query.data?.length && <p className="py-4 text-center text-sm text-gray-500">{s.empty}</p>}
        {!canSelectImage && <p className="text-xs text-amber-700">{s.imageLimit}</p>}
        <div className="grid max-h-72 grid-cols-2 gap-2 overflow-y-auto">
          {(query.data ?? []).filter(item => item.name.toLocaleLowerCase().includes(search.toLocaleLowerCase())).map(item => <div key={item.id} className="rounded-xl border border-gray-200 p-2">
            <button type="button" disabled={!canSelectImage} className="flex w-full cursor-pointer flex-col items-center gap-2 rounded p-2 hover:bg-indigo-50 disabled:opacity-40"
              aria-label={item.name} onClick={() => onSelect({ kind: 'IMAGE', imageUrl: item.imageUrl, alt: '[image-not-available]' })}>
              <img src={item.imageUrl} alt="" className="h-16 w-16 object-contain" />
              <span className="w-full truncate text-xs">{item.name}</span>
            </button>
            <div className="flex justify-end gap-2">
              <button type="button" aria-label={`${s.rename} ${item.name}`} className="cursor-pointer rounded p-1 text-gray-500 hover:text-indigo-600" onClick={() => { setEditing(item.id); setEditName(item.name); setDeleting(null); }}><Pencil className="h-4 w-4" /></button>
              <button type="button" aria-label={`${s.remove} ${item.name}`} className="cursor-pointer rounded p-1 text-gray-500 hover:text-red-600" onClick={() => { setDeleting(item.id); setEditing(null); }}><Trash2 className="h-4 w-4" /></button>
            </div>
            {editing === item.id && <div className="space-y-2">
              <Input size="sm" aria-label={s.name} maxLength={80} value={editName} onChange={event => setEditName(event.target.value)} />
              <Button type="button" size="xs" disabled={!editName.trim()} onClick={() => run(async () => { await rename.mutateAsync({ id: item.id, name: editName.trim() }); setEditing(null); })}>{s.save}</Button>
              <button type="button" className="ml-2 cursor-pointer text-xs" onClick={() => setEditing(null)}>{s.cancel}</button>
            </div>}
            {deleting === item.id && <div className="space-y-2">
              <p className="text-xs">{s.confirmRemove}</p>
              <Button type="button" size="xs" onClick={() => run(async () => { await remove.mutateAsync(item.id); setDeleting(null); })}>{s.remove}</Button>
              <button type="button" className="ml-2 cursor-pointer text-xs" onClick={() => setDeleting(null)}>{s.cancel}</button>
            </div>}
          </div>)}
        </div>
      </>}
    </>}
  </fieldset>;
}
